/**
 * Analytics-events från batterikalkyl-appen.
 *
 * Alla events är PSEUDONYMISERADE — ingen e-post, namn, IP eller exakt
 * adress lagras. user_pseudonym är hash av tibber_home_id + server-side
 * salt, vilket gör det omöjligt att återgå till en konkret person utan
 * tillgång till backend-secret + Tibber-konto.
 *
 * Tre typer av events:
 * - profile: hushållets förbrukningsprofil (en gång per session)
 * - scenario: konvergerad scenario-beräkning (många per session)
 * - session_end: engagemang-summering (en gång per session)
 */

export type AnalyticsEventType =
  | "profile"
  | "scenario"
  | "session_end"
  | "wizard_funnel";

export type ProductVertical =
  | "battery"
  | "heatpump"
  | "solar"
  | "ev_charger"
  | "windows"
  | "insulation";

export interface AnalyticsEventBase {
  /** Lokal session-id (UUID, lagras i sessionStorage, rensas när flik stängs) */
  sessionId: string;
  /**
   * Pseudonym = SHA256(tibberHomeId + serverSalt). Möjliggör aggregation
   * över sessioner från samma användare utan att avslöja identiteten.
   * Null om ingen Tibber-koppling finns.
   */
  userPseudonym: string | null;
  /** ISO timestamp när eventet skapades på klienten */
  clientTimestamp: string;
  /** Klientens user agent — endast för plattformsstatistik */
  userAgent?: string;
  /**
   * Vilken produktvertikal eventet hör till. Default "battery" för bakåt-
   * kompatibilitet. Sätts av klienten — alla nya kalkylatorer ska skicka
   * sin vertikal explicit.
   */
  productVertical?: ProductVertical;
}

/**
 * Datakälla för profilen. Avgör om datan får användas för att bygga
 * aggregerade typprofiler i profilbanken.
 *
 *   tibber     — verklig timserie från Tibber API (pålitlig)
 *   csv_upload — verklig timserie från användarladdad CSV (pålitlig)
 *   wizard     — användarens gissningar via wizard (icke-pålitlig,
 *                används bara för UX-telemetri, ALDRIG för profilbank)
 */
export type ProfileDataSource = "tibber" | "csv_upload" | "wizard";

/**
 * Profile event — skickas en gång per session.
 * Beskriver husets förbrukningsmönster, prisområde, säkring.
 * Används för att bygga typprofiler per region (endast pålitlig data).
 */
export interface AnalyticsProfileEvent extends AnalyticsEventBase {
  type: "profile";
  /** Var datan kommer ifrån. Bestämmer om den får aggregeras till typprofiler. */
  dataSource: ProfileDataSource;
  priceZone: string; // "SE1" | "SE2" | "SE3" | "SE4"
  fuseSizeAmp: number; // 16, 20, 25, 35, 50, etc
  annualImportKWh: number;
  annualExportKWh: number;
  hasSolar: boolean; // härleds från annualExportKWh > 0
  /** Månadsfördelning av import (12 siffror, jan-dec) i kWh */
  monthlyImportsKWh: number[];
  /**
   * Månadsspecifik dygnsfördelning av import. Matrisen är 12×24.
   * monthlyHourlyShape[m][h] = andel av månad m:s import som faller
   * på timme h. Summa per månad = 1.0 (eller 0 om månaden saknade data).
   *
   * Fångar att dygnsformen flyttar sig mellan säsongerna — december-
   * kvällstopp ligger runt 17-19 medan juli-eftermiddagstopp ligger
   * runt 13-15. Räcker tillsammans med monthlyImportsKWh för att
   * syntetisera en realistisk 8760-timserie för wizardanvändare.
   */
  monthlyHourlyShape: number[][];
  /** Tidszon (för geografisk grov-grupperingen) */
  timezone?: string;
  /** Antal månader data omfattar — för att veta dataqualitet */
  monthCount: number;
  /**
   * Två-siffrigt postnummer-prefix (länsnivå-grov-geografi). Frivilligt fält
   * från wizarden, eller härlett från Tibber-adress. Aldrig fullt postnummer
   * (5 siffror) som kunde identifiera exakt adress. För framtida sol-modul
   * där solinstrålning varierar geografiskt.
   */
  postalCodePrefix?: string;
}

/**
 * Wizard funnel event — skickas vid start, abandon, completion av wizarden.
 * Används för att förstå bortfall och vilka steg som filtrerar bort
 * användare. Ingen profil-data, bara stegnavigering.
 */
export interface AnalyticsWizardFunnelEvent extends AnalyticsEventBase {
  type: "wizard_funnel";
  step: "started" | "abandoned" | "completed";
  /** Hur lång tid användaren spenderade i wizarden (ms), null för started */
  durationMs?: number;
}

/**
 * Scenario event — skickas vid varje konvergerad (debouncad) simulering.
 * Beskriver vad användaren räknar på och vad resultatet blev.
 */
export interface AnalyticsScenarioEvent extends AnalyticsEventBase {
  type: "scenario";
  // Input
  batteryCapacityKWh: number;
  batteryMaxPowerKW: number;
  /** Investeringsbelopp grupperat i ranges (50000, 70000, 100000) — inte exakt */
  batteryInvestmentSekBucket: number;
  strategy: string; // "solar-only" | "predictive-24h" | "optimal-foresight" | ...
  ancillaryEnabled: boolean;
  powerTariffEnabled: boolean;
  lifetimePreset: string; // "conservative" | "neutral" | "optimistic"
  /** Lista över aktiva investeringsplan-typer */
  investmentPlanTypes: string[];
  // Output
  annualSavingsSek: number;
  paybackYears: number;
  totalLifetimeSavingsSek: number;
  totalRoiPercent: number;
  // Användarrelevanta signaler
  timingScore: number;
  sawWinterInsight: boolean;
}

/**
 * Session_end event — skickas via sendBeacon när användaren stänger fliken
 * eller är inaktiv. Sammanfattar sessionen.
 */
export interface AnalyticsSessionEndEvent extends AnalyticsEventBase {
  type: "session_end";
  sessionDurationSeconds: number;
  scenariosEvaluated: number;
  /** Den sista (sannolikt slutgiltiga) payback användaren såg */
  finalPaybackYears: number | null;
  finalAnnualSavings: number | null;
  /** Vilka detaljpaneler öppnades */
  openedDecomposition: boolean;
  openedTiming: boolean;
  openedSoftValues: boolean;
  /** Lade användaren till några investeringsplaner? */
  addedInvestmentPlans: boolean;
}

export type AnalyticsEvent =
  | AnalyticsProfileEvent
  | AnalyticsScenarioEvent
  | AnalyticsSessionEndEvent
  | AnalyticsWizardFunnelEvent;

/**
 * Bucketing av investeringsbelopp för att inte spara exakta priser.
 * Ger fortfarande användbar gruppering för analys.
 */
export function bucketInvestmentSek(value: number): number {
  if (value < 30_000) return 25_000;
  if (value < 50_000) return 40_000;
  if (value < 75_000) return 60_000;
  if (value < 100_000) return 85_000;
  if (value < 150_000) return 125_000;
  if (value < 200_000) return 175_000;
  return 250_000;
}
